import { useEffect, useState } from 'react';
import { useGameweek } from '../lib/useGameweek.ts';

/** Re-render cadence for the ticking label — a countdown that can be days
 * away doesn't need second-by-second precision, and a minute's drift on a
 * label granular to minutes is invisible. */
const TICK_MS = 30_000;

/**
 * The masthead's deadline readout (T-074) — the single most-checked number
 * in FPL, on screen on every route rather than nowhere. Reads `useGameweek`
 * directly rather than taking props, so it can sit beside the wordmark with
 * no wiring through `AppShell`'s children. Renders nothing until real
 * `deadline`/`phase` data exists — before any sync has run, or if the fetch
 * failed, `useGameweek` resolves to `null` and this stays inert; see that
 * hook for why a failure is never surfaced as an error here.
 */
export function DeadlineCountdown() {
  const meta = useGameweek();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  if (!meta || meta.deadline == null || meta.phase == null) return null;

  const { currentGameweek, deadline, phase, nextDeadline } = meta;
  const remaining = phase === 'pre' ? formatRemaining(deadline, now) : null;
  const nextRemaining = phase === 'post' && nextDeadline ? formatRemaining(nextDeadline, now) : null;

  return (
    <p className="deadline-countdown">
      <span className="deadline-countdown-gw">GW{currentGameweek}</span>
      {phase === 'pre' && <span>{remaining ? `${remaining} left` : 'Deadline passed'}</span>}
      {phase === 'live' && <span>Matches in progress</span>}
      {phase === 'post' && (
        <span>
          Gameweek finished
          {nextRemaining ? ` · next deadline in ${nextRemaining}` : ''}
        </span>
      )}
    </p>
  );
}

/** Days+hours when the deadline is far off, hours+minutes once it's close —
 * a raw single-unit duration ("4320 minutes") isn't something a manager
 * reads at a glance. Returns `null` once the deadline has passed (clock skew,
 * a stale response while `phase` is still `pre`) rather than a negative
 * countdown or a sentinel string spliced into the caller's sentence — callers
 * branch on that and choose their own wording. */
function formatRemaining(isoDeadline: string, now: number): string | null {
  const diffMs = new Date(isoDeadline).getTime() - now;
  if (diffMs <= 0) return null;

  const totalMinutes = Math.floor(diffMs / 60_000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h ${minutes}m`;
}
